import { useEffect, useRef, useState } from 'react'
import { api } from '../api/client.ts'
import { Avatar } from './Avatar.tsx'
import { useAuth } from '../store/auth.ts'
import { messageTime } from '../lib/format.ts'
import { RichText } from '../lib/richText.tsx'

/**
 * ОБСУЖДЕНИЕ ЛЮБОГО ОБЪЕКТА
 *
 * Одна панель на все разделы: пункт списка покупок, фильм, приём врача,
 * подарок. Раздел передаёт, к чему относится обсуждение, — и всё.
 *
 * Разметка та же, что в переписке: человек не должен помнить, где
 * звёздочки работают, а где нет.
 */

type Comment = {
  id: string
  body: string
  parentId: string | null
  author: { id: string; displayName: string; avatarUrl: string | null }
  createdAt: string
  editedAt: string | null
}

export function CommentsPanel({
  entityType,
  entityId,
  canWrite = true,
}: {
  entityType: string
  entityId: string
  canWrite?: boolean
}) {
  const me = useAuth((s) => s.user)
  const [comments, setComments] = useState<Comment[] | null>(null)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const areaRef = useRef<HTMLTextAreaElement>(null)

  const load = () => {
    api<{ comments: Comment[] }>(`/api/comments/${entityType}/${entityId}`)
      .then((data) => setComments(data.comments))
      .catch(() => setComments([]))
  }

  useEffect(load, [entityType, entityId])

  const send = async () => {
    const body = text.trim()
    if (!body || busy) return

    setBusy(true)
    setError(null)
    setText('')
    try {
      const data = await api<{ comment: Comment }>(`/api/comments/${entityType}/${entityId}`, {
        method: 'POST',
        body: { body },
      })
      setComments((current) => [...(current ?? []), data.comment])
    } catch (e) {
      // Текст возвращаем: потерять набранное из-за сбоя сети обиднее всего
      setText(body)
      setError(e instanceof Error ? e.message : 'Не отправилось')
    } finally {
      setBusy(false)
      areaRef.current?.focus()
    }
  }

  const remove = async (id: string) => {
    setComments((current) => (current ?? []).filter((c) => c.id !== id))
    await api(`/api/comments/${id}`, { method: 'DELETE' }).catch(load)
  }

  return (
    <div className="comments">
      {comments === null && (
        <div className="center-screen" style={{ height: 60 }}>
          <div className="spinner" />
        </div>
      )}

      {comments?.length === 0 && <p className="comments-empty">Пока никто ничего не написал</p>}

      {comments?.map((comment) => (
        <div key={comment.id} className="comment">
          <Avatar
            id={comment.author.id}
            name={comment.author.displayName}
            url={comment.author.avatarUrl}
            size={30}
          />
          <div className="comment-body">
            <div className="comment-head">
              <span className="comment-author">{comment.author.displayName}</span>
              <span className="comment-time">{messageTime(comment.createdAt)}</span>
              {comment.author.id === me?.id && (
                <button
                  className="comment-remove"
                  onClick={() => void remove(comment.id)}
                  aria-label="Удалить"
                >
                  ✕
                </button>
              )}
            </div>
            <div className="comment-text">
              <RichText text={comment.body} />
            </div>
          </div>
        </div>
      ))}

      {canWrite && (
        <div className="comment-form">
          <textarea
            ref={areaRef}
            className="input"
            rows={2}
            placeholder="Добавить комментарий…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              // На компьютере Enter отправляет, как в переписке
              if (e.key === 'Enter' && !e.shiftKey && !window.matchMedia('(pointer: coarse)').matches) {
                e.preventDefault()
                void send()
              }
            }}
            maxLength={2000}
          />
          <button className="btn-primary" onClick={() => void send()} disabled={!text.trim() || busy}>
            {busy ? '…' : 'Отправить'}
          </button>
        </div>
      )}

      {error && <div className="sheet-error">{error}</div>}
    </div>
  )
}
